import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../api/axios";

const InquiryForm = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [form, setForm] = useState({
    staff_id: "",
    customer_id: "",
    branch_id: "",
    inquiry_type: "General",
    subject: "",
    description: "",
    priority: "MEDIUM", // match enum values
    source: "Web",
    due_date: "",
    email: "", // Added email field
  });

  const [loading, setLoading] = useState(true);

  // Load dropdown data
  useEffect(() => {
    (async () => {
      try {
        const [staffRes, customersRes, branchesRes] = await Promise.all([
          axios.get("staff/"),
          axios.get("customers/"),
          axios.get("branches/"),
        ]);

        setStaff(staffRes.data || []);
        setCustomers(customersRes.data || []);
        
        // Filter branches to only show Rekonic UAE and Rekonic KSA
        const allBranches = branchesRes.data || [];
        const filteredBranches = allBranches.filter(b => 
          b.name === "Rekonic UAE" || b.name === "Rekonic KSA"
        );
        setBranches(filteredBranches);

        if (isEditMode) {
          const inquiryRes = await axios.get(`inquiries/${id}`);
          const data = inquiryRes.data;
          setForm({
            staff_id: data.staff_id || "",
            customer_id: data.customer_id || "",
            branch_id: data.branch_id || "",
            inquiry_type: data.inquiry_type || "",
            subject: data.subject || "",
            description: data.description || "",
            priority: data.priority ? data.priority.toUpperCase() : "MEDIUM",
            source: data.source || "Web",
            due_date: data.due_date ? new Date(data.due_date).toISOString().slice(0, 16) : "",
            email: data.email || "",
          });
        }
      } catch (e) {
        console.error("Data load error:", e);
        alert("Could not load form data");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditMode]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    const payload = {
      staff_id: parseInt(form.staff_id),
      customer_id: form.customer_id ? parseInt(form.customer_id) : null,
      branch_id: parseInt(form.branch_id), // Now backend supports it
      inquiry_type: form.inquiry_type,
      subject: form.subject,
      description: form.description || null,
      priority: form.priority.charAt(0) + form.priority.slice(1).toLowerCase(), // 'MEDIUM' → 'Medium'
      source: form.source || null,
      due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      email: form.email || null,
    };

    if (isEditMode) {
      const res = await axios.put(`inquiries/${id}`, payload);
      console.log("Inquiry updated:", res.data);
      alert("Inquiry updated successfully!");
      navigate("/dashboard/inquiries/list");
    } else {
      const res = await axios.post("inquiries/", payload);
      console.log("Inquiry created:", res.data);
      alert("Inquiry saved successfully!");
      setForm({
        staff_id: "",
        customer_id: "",
        branch_id: "",
        inquiry_type: "",
        subject: "",
        description: "",
        priority: "MEDIUM",
        source: "Web",
        due_date: "",
        email: "",
      });
    }
  } catch (e) {
    console.error("Save inquiry error:", e.response || e);
    alert(
      e.response?.data?.detail ||
      e.response?.data ||
      "Error creating inquiry. Check console."
    );
  }
};


  if (loading) return <p>Loading data…</p>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>{isEditMode ? "Edit Inquiry" : "Create New Inquiry"}</h4>
        <button className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          {/* Staff */}
          <div className="col-md-6">
            <label className="form-label">Staff *</label>
            <select
              name="staff_id"
              className="form-select"
              value={form.staff_id}
              onChange={handleChange}
              required
            >
              <option value="">Select staff</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {`${s.first_name || s.name} ${s.last_name || ""}`.trim()}
                </option>
              ))}
            </select>
          </div>

          {/* Customer */}
          <div className="col-md-6">
            <label className="form-label">Customer (optional)</label>
            <select
              name="customer_id"
              className="form-select"
              value={form.customer_id}
              onChange={handleChange}
            >
              <option value="">No customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.full_name || c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Branch */}
        <div className="col-md-4 mb-3">
          <label className="form-label">Branch *</label>
          <select
            name="branch_id"
            className="form-select"
            value={form.branch_id}
            onChange={handleChange}
            required
          >
            <option value="">Select branch</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label">Type *</label>
          <select
            name="inquiry_type"
            className="form-select"
            value={form.inquiry_type}
            onChange={handleChange}
            required
          >
            <option value="General">General</option>
            <option value="Sales">Sales</option>
            <option value="Support">Support</option>
            <option value="Feedback">Feedback</option>
          </select>
        </div>

        {/* Priority */}
        <div className="col-md-4 mb-3">
          <label className="form-label">Priority</label>
          <select
            name="priority"
            className="form-select"
            value={form.priority}
            onChange={handleChange}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="URGENT">Urgent</option>
          </select>
        </div>

        {/* Source */}
        <div className="col-md-4 mb-3">
          <label className="form-label">Source</label>
          <select
            name="source"
            className="form-select"
            value={form.source}
            onChange={handleChange}
          >
            <option>Web</option>
            <option>Email</option>
            <option>Phone</option>
            <option>Walk-in</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Subject *</label>
          <input
            type="text"
            name="subject"
            className="form-control"
            value={form.subject}
            onChange={handleChange}
            required
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <label className="form-label">Email (Optional)</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={form.email}
            onChange={handleChange}
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={3}
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </div>

        {/* Due Date */}
        <div className="mb-3">
          <label className="form-label">Due Date</label>
          <input
            type="datetime-local"
            className="form-control"
            name="due_date"
            value={form.due_date}
            onChange={handleChange}
          />
        </div>

        <button type="submit" className="btn btn-primary">
          {isEditMode ? "Update Inquiry" : "Submit Inquiry"}
        </button>
      </form>
    </div>
  );
};

export default InquiryForm;
